import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { UpcomingDateErrors } from '../utils/upcomingDate';

interface UpcomingDateEntryFieldProps {
  day: string;
  month: string;
  year: string;
  errors: UpcomingDateErrors;
  onDayChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onMonthChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onYearChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDayInteract?: () => void;
  onMonthInteract?: () => void;
  onYearInteract?: () => void;
  readOnly?: boolean;
  compact?: boolean;
}

const UpcomingDateEntryField: React.FC<UpcomingDateEntryFieldProps> = ({
  day,
  month,
  year,
  errors,
  onDayChange,
  onMonthChange,
  onYearChange,
  onDayInteract,
  onMonthInteract,
  onYearInteract,
  readOnly = false,
  compact = false,
}) => {
  const fontSize = compact ? '1.25rem' : '3rem';
  const widthMap = compact ? { day: '48px', month: '72px', year: '92px' } : { day: '80px', month: '120px', year: '150px' };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: compact ? 1 : 2, mb: compact ? 1.5 : 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <TextField
          value={day}
          onChange={onDayChange}
          onFocus={onDayInteract}
          onClick={onDayInteract}
          onTouchStart={onDayInteract}
          placeholder="DD"
          variant="standard"
          error={!!errors.day}
          inputProps={{
            style: {
              fontSize,
              fontWeight: 600,
              textAlign: 'center',
              width: widthMap.day,
            },
            maxLength: 2,
            readOnly,
          }}
          sx={{ '& .MuiInput-root': { fontSize } }}
        />
        {errors.day && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.day}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <TextField
          value={month}
          onChange={onMonthChange}
          onFocus={onMonthInteract}
          onClick={onMonthInteract}
          onTouchStart={onMonthInteract}
          placeholder="MMM"
          variant="standard"
          error={!!errors.month}
          inputProps={{
            style: {
              fontSize,
              fontWeight: 600,
              textAlign: 'center',
              width: widthMap.month,
            },
            maxLength: 3,
            readOnly,
          }}
          sx={{ '& .MuiInput-root': { fontSize } }}
        />
        {errors.month && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.month}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <TextField
          value={year}
          onChange={onYearChange}
          onFocus={onYearInteract}
          onClick={onYearInteract}
          onTouchStart={onYearInteract}
          placeholder="YYYY"
          variant="standard"
          error={!!errors.year}
          inputProps={{
            style: {
              fontSize,
              fontWeight: 600,
              textAlign: 'center',
              width: widthMap.year,
            },
            maxLength: 4,
            readOnly,
          }}
          sx={{ '& .MuiInput-root': { fontSize } }}
        />
        {errors.year && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.year}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UpcomingDateEntryField;
